import type { Data } from '../types';

type TeamTone = 'ct' | 't';

type FlashMetric = {
  name: string;
  team: string;
  avgDuration: number;
  avgDamage: number;
  enemyCount: number;
};

type FlashDot = {
  name: string;
  x: number;
  y: number;
  tone: TeamTone;
  avgDuration: number;
  avgDamage: number;
};

const exampleMetrics: FlashMetric[] = [
  { name: 'Nova', team: 'CT', avgDuration: 2.4, avgDamage: 42, enemyCount: 6 },
  { name: 'Orbit', team: 'T', avgDuration: 1.7, avgDamage: 28, enemyCount: 4 },
  { name: 'Lyric', team: 'CT', avgDuration: 2.9, avgDamage: 55, enemyCount: 8 },
  { name: 'Rift', team: 'T', avgDuration: 1.2, avgDamage: 18, enemyCount: 3 },
  { name: 'Axel', team: 'CT', avgDuration: 2.1, avgDamage: 33, enemyCount: 5 },
  { name: 'Vega', team: 'T', avgDuration: 1.9, avgDamage: 47, enemyCount: 6 },
];

const normalizeSide = (value: string | undefined): TeamTone | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'ct' || normalized.includes('counter')) return 'ct';
  if (normalized === 't' || normalized.includes('terror')) return 't';
  return null;
};

const buildTeamSideByName = (data: Data | null) => {
  const tones = new Map<string, TeamTone>();
  if (!data) return tones;
  for (const [name, team] of Object.entries(data.teams ?? {})) {
    const tone = normalizeSide(team.startedOn);
    if (tone) tones.set(name, tone);
  }
  return tones;
};

const buildFallbackTones = (teams: string[]) => {
  const names = Array.from(new Set(teams)).sort((a, b) => a.localeCompare(b));
  const tones = new Map<string, TeamTone>();
  for (let index = 0; index < names.length; index += 1) {
    const name = names[index];
    tones.set(name, index % 2 === 0 ? 'ct' : 't');
  }
  return tones;
};

const buildFlashMetrics = (data: Data | null): FlashMetric[] => {
  if (!data) return [];

  return Object.entries(data.playerstats)
    .map(([name, stats]) => {
      const enemy = stats.flashes.blinded.enemy;
      const enemyCount = enemy.amount;
      const avgDuration = enemyCount > 0 ? enemy.totalDuration / enemyCount : 0;
      const avgDamage = enemyCount > 0 ? enemy.damage / enemyCount : 0;

      return {
        name,
        team: stats.team,
        avgDuration,
        avgDamage,
        enemyCount,
      };
    })
    .filter((metric) => metric.enemyCount > 0);
};

const getMaxMetrics = (metrics: FlashMetric[]) => {
  if (metrics.length === 0) {
    return { maxDuration: 0, maxDamage: 0 };
  }

  return {
    maxDuration: Math.max(...metrics.map((metric) => metric.avgDuration)),
    maxDamage: Math.max(...metrics.map((metric) => metric.avgDamage)),
  };
};

const clampPercent = (value: number, min = 0, max = 100) =>
  Math.min(max, Math.max(min, value));

const buildFlashDots = (
  metrics: FlashMetric[],
  data: Data | null,
  maxDuration: number,
  maxDamage: number,
): FlashDot[] => {
  if (metrics.length === 0) return [];

  const safeMaxDuration = maxDuration > 0 ? maxDuration : 1;
  const safeMaxDamage = maxDamage > 0 ? maxDamage : 1;
  const teamSideByName = buildTeamSideByName(data);
  const fallbackTones = buildFallbackTones(metrics.map((metric) => metric.team));

  return metrics.map((metric) => {
    const tone =
      teamSideByName.get(metric.team) ??
      normalizeSide(metric.team) ??
      fallbackTones.get(metric.team) ??
      'ct';
    const x = clampPercent((metric.avgDuration / safeMaxDuration) * 100);
    const y = clampPercent(100 - (metric.avgDamage / safeMaxDamage) * 100);

    return {
      name: metric.name,
      x,
      y,
      tone,
      avgDuration: metric.avgDuration,
      avgDamage: metric.avgDamage,
    };
  });
};

type FlashEffectivenessPanelProps = {
  isExample: boolean;
  data: Data | null;
};

export default function FlashEffectivenessPanel({ isExample, data }: FlashEffectivenessPanelProps) {
  const metrics = data ? buildFlashMetrics(data) : isExample ? exampleMetrics : [];
  const { maxDuration, maxDamage } = getMaxMetrics(metrics);
  const dots = buildFlashDots(metrics, data, maxDuration, maxDamage);
  const hasData = dots.length > 0;
  const statLabel = hasData ? `${dots.length} players used flashes` : 'No flash data';
  const durationMid = maxDuration / 2;
  const damageMid = maxDamage / 2;
  const formatValue = (value: number) => value.toFixed(1);
  const plotInset = { top: 24, right: 16, bottom: 36, left: 56 };
  const plotStyle = {
    top: plotInset.top,
    right: plotInset.right,
    bottom: plotInset.bottom,
    left: plotInset.left,
  };

  return (
    <div
      className={`relative col-span-7 overflow-visible rounded-[20px] border border-stroke bg-card p-5 shadow-[0_18px_32px_rgba(0,0,0,0.45)] animate-rise ${
        !isExample && !hasData ? 'opacity-70' : ''
      } max-[960px]:col-span-1`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold">Flash effectiveness map</h3>
          <p className="mt-1 text-[13px] text-muted">
            Avg enemy flash duration vs damage dealt while blinded
          </p>
        </div>
        <span className="rounded-full bg-accent/20 px-2.5 py-1 text-[12px] font-semibold text-accent">
          {statLabel}
        </span>
      </div>
      <div className="mt-4 grid gap-3">
        <div className="relative min-h-[300px] overflow-visible rounded-2xl border border-[rgba(60,27,46,0.9)] bg-[linear-gradient(135deg,rgba(45,22,38,0.95),rgba(31,13,26,0.95))] before:absolute before:inset-0 before:content-[''] before:bg-[repeating-linear-gradient(90deg,rgba(255,254,62,0.06)_0,rgba(255,254,62,0.06)_1px,transparent_1px,transparent_24px),repeating-linear-gradient(180deg,rgba(255,254,62,0.06)_0,rgba(255,254,62,0.06)_1px,transparent_1px,transparent_24px)]">
          <div className="absolute overflow-visible" style={plotStyle}>
            <span className="pointer-events-none absolute left-0 top-0 bottom-0 w-px bg-[rgba(255,254,62,0.28)]" />
            <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px bg-[rgba(255,254,62,0.28)]" />
            <span className="pointer-events-none absolute left-0 top-0 h-px w-3 bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-px w-3 bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute left-0 bottom-0 h-px w-3 bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute left-0 bottom-0 h-3 w-px bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute left-1/2 bottom-0 -translate-x-1/2 h-3 w-px bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute right-0 bottom-0 h-3 w-px bg-[rgba(255,254,62,0.35)]" />
            <span className="pointer-events-none absolute -left-[110px] top-1/2 -translate-y-1/2 -rotate-90 text-[11px] uppercase tracking-[1px] text-muted">
              Avg blinded damage
            </span>
            <span className="pointer-events-none absolute bottom-[-33px] left-1/2 -translate-x-1/2 text-[11px] uppercase tracking-[1px] text-muted">
              Avg enemy flash duration (s)
            </span>
            {hasData && (
              <>
                <span className="pointer-events-none absolute -left-8 top-0 -translate-y-1/2 text-[10px] text-muted">
                  {formatValue(maxDamage)}
                </span>
                <span className="pointer-events-none absolute -left-8 top-1/2 -translate-y-1/2 text-[10px] text-muted">
                  {formatValue(damageMid)}
                </span>
                <span className="pointer-events-none absolute left-0 bottom-[-18px] -translate-x-1/2 text-[10px] text-muted">
                  0.0
                </span>
                <span className="pointer-events-none absolute left-1/2 bottom-[-18px] -translate-x-1/2 text-[10px] text-muted">
                  {formatValue(durationMid)}
                </span>
                <span className="pointer-events-none absolute right-0 bottom-[-18px] text-[10px] text-muted">
                  {formatValue(maxDuration)}
                </span>
              </>
            )}
            {dots.map((dot) => (
              <span
                key={dot.name}
                className="group absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
              >
                <span
                  className={`block h-2.5 w-2.5 rounded-full shadow-[0_8px_14px_rgba(0,0,0,0.45)] ${
                    dot.tone === 'ct' ? 'bg-ct' : 'bg-t'
                  }`}
                />
                <span className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-full -mt-2 whitespace-nowrap rounded-lg border border-stroke bg-[rgba(23,10,19,0.9)] px-2 py-1 text-[11px] font-semibold text-ink opacity-0 transition group-hover:opacity-100">
                  <span className="block text-ink">{dot.name}</span>
                  <span className="mt-0.5 block text-[10px] text-muted">
                    Avg duration: {dot.avgDuration.toFixed(2)}s
                  </span>
                  <span className="mt-0.5 block text-[10px] text-muted">
                    Avg blinded dmg: {dot.avgDamage.toFixed(1)}
                  </span>
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
      {!isExample && !hasData && (
        <div className="absolute inset-4 grid place-items-center rounded-2xl border border-dashed border-accent/35 bg-[rgba(31,13,26,0.92)] text-center text-[14px] font-semibold text-muted">
          Upload a log file to populate the flash map.
        </div>
      )}
    </div>
  );
}
